import {
  DEFAULT_SOUND_ID,
  DEFAULT_SOUND_VOLUME
} from '@/constants/notificationSounds';
import { type ISoundSettings } from '@/types/ISoundSettings';
import { isRecord } from '@/utils/isRecord';

export const SOUND_SETTINGS_KEY = 'orderSoundSettings';

const DEFAULT_SOUND_SETTINGS: ISoundSettings = {
  enabled: true,
  soundId: DEFAULT_SOUND_ID,
  volume: DEFAULT_SOUND_VOLUME
};

const isSoundSettings = (value: unknown): value is ISoundSettings =>
  isRecord(value) &&
  typeof value.enabled === 'boolean' &&
  typeof value.soundId === 'string' &&
  typeof value.volume === 'number';

/**
 * Ограничивает громкость допустимым диапазоном `0..1`.
 *
 * @param volume - произвольное значение громкости
 * @returns значение, зажатое в пределах `[0, 1]`
 */
const clampVolume = (volume: number): number =>
  Math.min(1, Math.max(0, volume));

/**
 * Читает настройки звука из localStorage. При отсутствии или повреждении
 * записи возвращает дефолтные настройки (звук включён, дефолтная мелодия).
 * Это UI-предпочтение пользователя — при логауте не очищается.
 *
 * @returns актуальные настройки звука
 */
export const readSoundSettings = (): ISoundSettings => {
  const raw = localStorage.getItem(SOUND_SETTINGS_KEY);

  if (raw === null) {
    return DEFAULT_SOUND_SETTINGS;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_SOUND_SETTINGS;
  }

  if (!isSoundSettings(parsed)) {
    return DEFAULT_SOUND_SETTINGS;
  }

  return { ...parsed, volume: clampVolume(parsed.volume) };
};

/**
 * Сохраняет настройки звука в localStorage.
 *
 * @param settings - настройки звука для персиста
 */
export const saveSoundSettings = (settings: ISoundSettings): void => {
  const payload: ISoundSettings = {
    ...settings,
    volume: clampVolume(settings.volume)
  };
  localStorage.setItem(SOUND_SETTINGS_KEY, JSON.stringify(payload));
};
